import React from 'react';
import { TrendingUp, ArrowLeft } from 'lucide-react';

/**
 * The legal pages: Terms of Service, Privacy Policy, and the Risk Disclaimer.
 *
 * <p>Static content, deliberately. These render outside the auth gate (a visitor must be able
 * to read them BEFORE creating an account, and Stripe links to them from checkout), they must
 * never fail to load, and versioning them in git alongside the product that they describe is
 * exactly the audit trail you want when someone asks what the terms said on a given date.</p>
 *
 * <p>The trading-risk disclaimer is a separate first-class page rather than a clause buried in
 * the terms: for a product whose whole purpose is trading automation, "you can lose money and
 * nothing here is investment advice" is the single most important sentence on the site.</p>
 */

const LAST_UPDATED = 'August 24, 2026';
const OPERATOR = 'TradeExecute';
const CONTACT_EMAIL = 'support@tradeexecute.com';

type LegalDoc = 'terms' | 'privacy' | 'risk';

function readDoc(): LegalDoc {
  const raw = (window.location.hash || '').replace(/^#\/?/, '');
  const second = raw.split('?')[0].split('/')[1] ?? '';
  if (second === 'privacy') return 'privacy';
  if (second === 'risk') return 'risk';
  return 'terms';
}

const NAV: { id: LegalDoc; label: string; hash: string }[] = [
  { id: 'terms', label: 'Terms of Service', hash: '#/legal/terms' },
  { id: 'privacy', label: 'Privacy Policy', hash: '#/legal/privacy' },
  { id: 'risk', label: 'Risk Disclaimer', hash: '#/legal/risk' },
];

/** Shared typographic wrapper so all three documents read identically. */
const Doc: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <article className="space-y-4">
    <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
    <p className="text-xs text-muted-foreground">Last updated: {LAST_UPDATED}</p>
    <div className="space-y-4 text-sm leading-relaxed text-muted-foreground [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-foreground [&_h2]:mt-6 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_strong]:text-foreground">
      {children}
    </div>
  </article>
);

const Terms: React.FC = () => (
  <Doc title="Terms of Service">
    <p>
      These terms govern your use of {OPERATOR} (the "Service"), available at tradeexecute.com.
      By creating an account or using the Service you agree to them. If you do not agree, do not
      use the Service.
    </p>

    <h2>1. What the Service is — and is not</h2>
    <p>
      {OPERATOR} provides market-scanning, charting, strategy backtesting, paper-trading
      simulation, AI-assisted analysis, and tools that can route orders to brokerage accounts
      <strong> that you own and control</strong>. {OPERATOR} is a software tool.{' '}
      <strong>
        It is not a broker-dealer, not an investment adviser, and not a source of investment
        advice.
      </strong>{' '}
      Nothing in the Service — including scanner results, backtest statistics, and AI assistant
      output — is a recommendation to buy or sell any security or other instrument. See the{' '}
      <a className="text-primary hover:underline" href="#/legal/risk">Risk Disclaimer</a>, which
      is part of these terms.
    </p>

    <h2>2. Your account</h2>
    <ul>
      <li>You must provide accurate information and keep your password confidential. You are
        responsible for all activity under your account.</li>
      <li>One person per account. Accounts may not be shared, resold, or pooled to circumvent
        plan limits.</li>
      <li>You must be at least 18 years old and legally able to enter into contracts.</li>
    </ul>

    <h2>3. Subscriptions and billing</h2>
    <ul>
      <li>Paid plans renew monthly and are billed in advance through Stripe, our payment
        processor. We never see or store your card details.</li>
      <li>You can cancel at any time from the billing page; access continues to the end of the
        period already paid for. Except where required by law, payments are non-refundable.</li>
      <li>Each plan carries usage limits (scanners, strategies, backtest compute, AI usage, and
        similar). Limits are enforced by the Service and may be adjusted; material reductions to
        a paid plan will be announced in advance.</li>
      <li>We may change prices with at least 14 days' notice; changes apply from your next
        renewal.</li>
    </ul>

    <h2>4. Brokerage connections and trading</h2>
    <ul>
      <li>Live and paper trading features operate on brokerage accounts you hold with third
        parties (for example Alpaca, FXCM, or IG). Your relationship with a broker is governed
        by that broker's own terms; {OPERATOR} is not a party to it.</li>
      <li>When you supply broker API credentials, you authorize the Service to place, modify,
        and close orders on that account according to the rules <strong>you</strong> configure —
        including automated rules that act without further confirmation while enabled.</li>
      <li>You are solely responsible for every order the Service submits on your behalf, for
        monitoring automations you enable, and for complying with your broker's rules and the
        laws that apply to you, including any pattern-day-trading or margin requirements.</li>
      <li>Real-money automation is off by default and requires your explicit opt-in.</li>
    </ul>

    <h2>5. Market data and fair use</h2>
    <ul>
      <li>Market data shown in the Service comes from third-party providers and is provided for
        your personal use within the Service only. You may not redistribute, resell, or
        systematically extract it (no scraping, bulk export, or feeding it to other systems).</li>
      <li>Data may be delayed, incomplete, or wrong. Verify independently before acting on it.</li>
      <li>We may apply technical measures (rate limits, quotas) to keep the Service usable for
        everyone; attempting to evade them is a breach of these terms.</li>
    </ul>

    <h2>6. AI features</h2>
    <p>
      The AI assistant generates output with large language models. Its output can be wrong,
      incomplete, or outdated, even when it sounds confident, and is not investment advice.
      Where your plan permits the assistant to act (creating strategies, running backtests,
      placing paper or live trades), those actions are attributed to you and run under your
      plan's limits and your trading configuration. Conversations you send to the assistant are
      processed by a third-party AI provider (see the Privacy Policy).
    </p>

    <h2>7. Acceptable use</h2>
    <p>You agree not to:</p>
    <ul>
      <li>break the law, or use the Service to manipulate markets in any way;</li>
      <li>probe, disrupt, overload, or attempt to gain unauthorized access to the Service or
        other users' data;</li>
      <li>reverse-engineer the Service or use it to build a competing data product;</li>
      <li>upload malicious code or use another person's credentials.</li>
    </ul>

    <h2>8. Availability</h2>
    <p>
      The Service is provided on an "as available" basis. We work to keep it reliable, but we do
      not promise uninterrupted operation, and maintenance, third-party outages (brokers, data
      providers, AI providers), or failures may make it unavailable — including at moments when
      markets are moving. Do not rely on the Service as your only means of monitoring or exiting
      positions.
    </p>

    <h2>9. Disclaimer of warranties</h2>
    <p>
      To the fullest extent permitted by law, the Service is provided <strong>"as is" and "as
      available"</strong>, without warranties of any kind, express or implied, including
      merchantability, fitness for a particular purpose, accuracy, and non-infringement.
    </p>

    <h2>10. Limitation of liability</h2>
    <p>
      To the fullest extent permitted by law, {OPERATOR} and its operators will not be liable
      for any trading losses, lost profits, lost data, or any indirect, incidental, special,
      consequential, or punitive damages arising from your use of the Service — including losses
      caused by automation you enabled, data errors, delays, or downtime. Our total aggregate
      liability for all claims relating to the Service is limited to the amount you paid us in
      the twelve months before the event giving rise to the claim. Nothing in these terms
      excludes liability that cannot be excluded by law.
    </p>

    <h2>11. Termination</h2>
    <p>
      You may stop using the Service and cancel at any time. We may suspend or terminate
      accounts that breach these terms, create risk for the Service or other users, or are
      required to be closed by law. On termination we may delete your data after a reasonable
      period; you can request deletion earlier (see the Privacy Policy).
    </p>

    <h2>12. Changes to these terms</h2>
    <p>
      We may update these terms as the Service evolves. For material changes we will give notice
      (by email or in the app) at least 14 days before they take effect. Continuing to use the
      Service after that date means you accept the updated terms.
    </p>

    <h2>13. Contact</h2>
    <p>
      Questions about these terms: <a className="text-primary hover:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
    </p>
  </Doc>
);

const Privacy: React.FC = () => (
  <Doc title="Privacy Policy">
    <p>
      This policy explains what {OPERATOR} collects, why, and what happens to it. The short
      version: we collect what the product needs to work, we don't sell it, and payment card
      details never touch our servers.
    </p>

    <h2>1. What we collect</h2>
    <ul>
      <li><strong>Account data</strong> — your email address and a hashed password (we store
        only the hash, never the password itself).</li>
      <li><strong>Security data</strong> — login history including IP address, browser
        user-agent, and timestamps, kept to protect your account and investigate abuse.</li>
      <li><strong>Product content</strong> — the scanners, strategies, automation rules,
        backtest runs, paper/live trade records, watchlists, and settings you create.</li>
      <li><strong>Usage metering</strong> — counters for plan enforcement (backtest compute
        minutes, AI actions, and similar).</li>
      <li><strong>Broker credentials</strong> — if you connect a brokerage account, the API
        keys you supply are stored server-side and used solely to operate the trading features
        you configure.</li>
      <li><strong>AI conversations</strong> — messages you send to the AI assistant and the
        account context it needs to answer (for example your strategy list or positions when
        you ask about them).</li>
      <li><strong>Billing status</strong> — your Stripe customer and subscription identifiers
        and subscription state. Card numbers are collected by Stripe on Stripe's pages and are
        never visible to us.</li>
    </ul>

    <h2>2. What we use it for</h2>
    <ul>
      <li>Operating the Service: authentication, running your scanners and automations, showing
        your data back to you.</li>
      <li>Enforcing plan limits and preventing abuse.</li>
      <li>Billing, through Stripe.</li>
      <li>Answering support requests.</li>
      <li>Aggregate, non-identifying statistics to improve the product.</li>
    </ul>
    <p>We do not sell your personal data, and we do not use it for third-party advertising.</p>

    <h2>3. Who it is shared with</h2>
    <ul>
      <li><strong>Stripe</strong> — payment processing (your email and subscription state).</li>
      <li><strong>Your broker(s)</strong> — orders and account queries you configure the
        Service to make on your behalf.</li>
      <li><strong>AI provider</strong> — messages to the AI assistant, and the minimal account
        context needed to answer them, are processed by our large-language-model provider to
        generate responses. Don't put sensitive information you wouldn't want processed this way
        into AI chats.</li>
      <li><strong>Market-data providers</strong> — symbol-level requests needed to serve you
        quotes and history.</li>
      <li>Authorities, where the law genuinely requires it.</li>
    </ul>

    <h2>4. Cookies and local storage</h2>
    <p>
      The app uses browser local storage for your session tokens and interface preferences
      (theme, layout). We do not use third-party advertising or analytics cookies.
    </p>

    <h2>5. Retention</h2>
    <ul>
      <li>Account and product data: kept while your account exists.</li>
      <li>Backtest runs: retained per your plan's retention window, then pruned.</li>
      <li>Login history and audit records: kept for security purposes for up to 12 months.</li>
      <li>Billing records: kept as long as tax and accounting law requires.</li>
    </ul>

    <h2>6. Security</h2>
    <p>
      Traffic is encrypted with TLS; passwords are stored as salted hashes; access to production
      systems is restricted; trading actions are audit-logged. No system is perfectly secure —
      use a strong, unique password.
    </p>

    <h2>7. Your rights</h2>
    <p>
      You can access and export the content you created in the app, and you can ask us to
      correct or delete your personal data. To delete your account and its data, email{' '}
      <a className="text-primary hover:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>{' '}
      from your account address. Depending on where you live you may have additional statutory
      rights (such as under the GDPR); we honour reasonable requests regardless of geography.
    </p>

    <h2>8. Children</h2>
    <p>The Service is not directed at children and may not be used by anyone under 18.</p>

    <h2>9. Changes</h2>
    <p>
      If this policy changes materially we will notify you by email or in the app before the
      change takes effect.
    </p>

    <h2>10. Contact</h2>
    <p>
      Privacy questions and requests: <a className="text-primary hover:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
    </p>
  </Doc>
);

const Risk: React.FC = () => (
  <Doc title="Risk Disclaimer">
    <p>
      <strong>
        Trading involves substantial risk of loss. You can lose some or all of the money you
        trade. Nothing in {OPERATOR} is investment advice.
      </strong>
    </p>

    <h2>No advice, no recommendations</h2>
    <p>
      {OPERATOR} is analytical software. Scanner results, strategy templates, backtest
      statistics, charts, and AI assistant output are informational tools — none of it is a
      recommendation to buy, sell, or hold anything, and none of it is tailored to your
      financial situation. {OPERATOR} is not a broker-dealer or a licensed investment adviser.
      Before trading, consider consulting one.
    </p>

    <h2>Backtests are hypothetical</h2>
    <p>
      Backtest and simulation results are produced with the benefit of hindsight on historical
      data. They do not represent real trading, and they systematically overstate what live
      trading achieves: they may not fully account for slippage, spreads, commissions, partial
      fills, halts, or the market impact of your own orders — and strategies can be overfit to
      the past. <strong>Past performance, real or simulated, does not indicate future
      results.</strong>
    </p>

    <h2>Automation cuts both ways</h2>
    <p>
      Automated rules act without asking you first — that is their purpose. A rule that is
      misconfigured, a data feed that misbehaves, or an outage at the wrong moment can produce
      trades and losses you did not anticipate. Keep position sizes appropriate, use the
      kill-switches provided, monitor what you enable, and never automate money you cannot
      afford to lose.
    </p>

    <h2>AI output can be wrong</h2>
    <p>
      The AI assistant can misread data, make arithmetic mistakes, and state incorrect things
      with confidence. Treat its output as a starting point to verify, never as a conclusion to
      act on.
    </p>

    <h2>Data and availability</h2>
    <p>
      Market data can be delayed, incomplete, or erroneous, and the Service can be unavailable
      — including during fast markets. Maintain the ability to view and manage your positions
      directly with your broker at all times.
    </p>

    <h2>Leveraged and volatile instruments</h2>
    <p>
      Forex, CFDs, and margined products carry amplified risk: losses can exceed your initial
      deposit. Low-priced and low-liquidity equities can gap, halt, and become impossible to
      exit at the price you expect.
    </p>

    <p>
      <strong>Only trade with capital you can afford to lose.</strong> By using {OPERATOR} you
      acknowledge that all trading decisions — including those executed by automations and AI
      features you enable — are your own, and that {OPERATOR} bears no responsibility for your
      trading results.
    </p>
  </Doc>
);

export const LegalPage: React.FC = () => {
  const [doc, setDoc] = React.useState<LegalDoc>(() => readDoc());

  React.useEffect(() => {
    const onHash = () => setDoc(readDoc());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // A legal page reached mid-scroll from another legal page should start at the top —
  // hash-only navigation doesn't reset scroll by itself.
  React.useEffect(() => { window.scrollTo(0, 0); }, [doc]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <a href="#/" className="flex items-center gap-2 text-sm font-semibold">
            <div className="h-6 w-6 rounded-md bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center">
              <TrendingUp className="h-3.5 w-3.5 text-white" />
            </div>
            <span>Trade<span className="text-primary">Execute</span></span>
          </a>
          <a href="#/" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to home
          </a>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <nav aria-label="Legal documents" className="mb-8 flex flex-wrap gap-2">
          {NAV.map(item => (
            <a
              key={item.id}
              href={item.hash}
              aria-current={doc === item.id ? 'page' : undefined}
              className={
                doc === item.id
                  ? 'rounded-full border border-primary bg-primary/10 px-3.5 py-1.5 text-xs font-medium text-primary'
                  : 'rounded-full border border-border px-3.5 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors'
              }
            >
              {item.label}
            </a>
          ))}
        </nav>

        {doc === 'privacy' ? <Privacy /> : doc === 'risk' ? <Risk /> : <Terms />}

        <footer className="mt-12 border-t border-border pt-4 text-xs text-muted-foreground">
          &copy; {new Date().getFullYear()} {OPERATOR}. All rights reserved.
        </footer>
      </div>
    </div>
  );
};

export default LegalPage;
